import { inject, Service } from '@angular/core';
import type { TranslateService } from '@ngx-translate/core';
import type {
  OnyxCustomAddress,
  OnyxDatePipe,
  OnyxPointOfInterestAddress,
  OnyxSuggestion,
  OnyxTime,
  OnyxTimeRange,
} from '@onyx/angular';
import { DateTime, Duration } from 'luxon';
import { map } from 'rxjs';
import { BasesService } from '../../dashboard/management-panel/bases/common/services/bases.service';
import { PaymentTermType } from '../../dashboard/management-panel/contractors/common/enums/payment-term-type';
import type { Contractor } from '../../dashboard/management-panel/contractors/common/interfaces/contractor';
import { PointOfInterestHelper } from '../../dashboard/management-panel/points-of-interest/common/helpers/point-of-interest.helper';
import { DAY_TIME_RANGE } from '../constants/common/day-time-range';
import type { BusinessHours } from '../interfaces/common/business-hours';
import { CommonHelper } from './common.helper';

@Service()
export class SuggestionHelper {
  private readonly baseService = inject(BasesService);

  public readonly baseSuggestions$ = this.baseService.listBases().pipe(
    map((bases): OnyxSuggestion<string>[] =>
      bases.slice(0, 2).map((base) => ({
        name: base.name,
        value: base.uuid,
      })),
    ),
  );

  public static getDateSuggestions = (
    translateService: TranslateService,
    datePipe: OnyxDatePipe,
    options?: {
      days?: number;
      future?: boolean;
      businessHours?: BusinessHours;
    },
  ): OnyxSuggestion<string>[] => {
    const { days = 2, future = true, businessHours } = options ?? {};

    const today = DateTime.now();
    const todayLabel =
      `(${translateService.instant('labels.today')})`.toLowerCase();
    const tomorrowLabel =
      `(${translateService.instant('labels.tommorow')})`.toLowerCase();
    const yesterdayLabel =
      `(${translateService.instant('labels.yesterday')})`.toLowerCase();

    const isDayOpen = (date: DateTime) => {
      if (!businessHours) return true;

      const openingHours = CommonHelper.getOpeningHours(date, businessHours);
      if (!openingHours) return false;

      return !(
        date.hasSame(today, 'day') &&
        `${today.hour}:${today.minute}:00` > openingHours.to
      );
    };

    const getLabel = (date: DateTime): string => {
      if (date.hasSame(today, 'day')) return todayLabel;
      if (date.hasSame(today.plus({ days: 1 }), 'day')) return tomorrowLabel;
      if (date.hasSame(today.minus({ days: 1 }), 'day')) return yesterdayLabel;
      return '';
    };

    const suggestions = [];
    let date = today;

    while (suggestions.length < days) {
      if (isDayOpen(date)) {
        suggestions.push({
          name: `${datePipe.transform(date.toISODate(), 'date-dot')} ${getLabel(date)}`,
          value: date.toISODate(),
        });
      }
      date = future ? date.plus({ days: 1 }) : date.minus({ days: 1 });
    }

    return suggestions;
  };

  public static getDelegationAddressSuggestions = (
    driverBaseAddress: OnyxCustomAddress | null,
    driverHomeAddress: OnyxCustomAddress | null,
    vehicleBaseAddress: OnyxCustomAddress | null,
  ): OnyxSuggestion<OnyxCustomAddress>[] => {
    const I18N = 'delegations.delegationPeriodForm';
    return [
      ...(driverBaseAddress
        ? [
            {
              name: `${I18N}.driverBase`,
              value: driverBaseAddress,
            },
          ]
        : []),
      ...(driverHomeAddress
        ? [
            {
              name: `${I18N}.driverHome`,
              value: driverHomeAddress,
            },
          ]
        : []),
      ...(vehicleBaseAddress
        ? [
            {
              name: `${I18N}.vehicleBase`,
              value: vehicleBaseAddress,
            },
          ]
        : []),
    ];
  };

  public static getContractorPointOfInterestAddressSuggestions(
    pointsOfInterest: Contractor['pointsOfInterest'],
    limit = 2,
  ): OnyxSuggestion<OnyxPointOfInterestAddress>[] {
    return pointsOfInterest.slice(0, limit).map(({ pointOfInterest }) => ({
      name: pointOfInterest.name,
      value: PointOfInterestHelper.getAddress(pointOfInterest),
    }));
  }

  public static getTimeSuggestions(): OnyxSuggestion<OnyxTime>[] {
    return [
      {
        name: 'labels.now',
        value: DateTime.now().toFormat('HH:mm:00') as OnyxTime,
      },
    ];
  }

  public static getTimeRangeSuggestions(): OnyxSuggestion<OnyxTimeRange>[] {
    return [
      {
        name: '00:00 - 23:59',
        value: DAY_TIME_RANGE,
      },
      {
        name: '06:00 - 22:00',
        value: { from: '06:00:00', to: '22:00:00' },
      },
    ];
  }

  public static getDurationSuggestions(): OnyxSuggestion<number>[] {
    return [
      {
        name: '1h',
        value: Duration.fromObject({ hours: 1 }).as('seconds'),
      },
      {
        name: '2h',
        value: Duration.fromObject({ hours: 2 }).as('seconds'),
      },
    ];
  }

  public static getPaymentTermSuggestions(
    sellDate: string,
    traditionalMail: boolean,
    termDays: number,
    termType: PaymentTermType,
    selectedTermDay: number | null,
    datePipe: OnyxDatePipe,
  ): OnyxSuggestion<string>[] {
    const sellDateTime = CommonHelper.addBusinessDays(
      sellDate,
      traditionalMail ? 2 : 0,
    );

    const weekdayMap: Partial<Record<PaymentTermType, number>> = {
      [PaymentTermType.MONDAY]: 1,
      [PaymentTermType.TUESDAY]: 2,
      [PaymentTermType.WEDNESDAY]: 3,
      [PaymentTermType.THURSDAY]: 4,
      [PaymentTermType.FRIDAY]: 5,
      [PaymentTermType.SATURDAY]: 6,
      [PaymentTermType.SUNDAY]: 7,
    };

    let paymentDate = sellDateTime;

    if (termType in weekdayMap) {
      const targetWeekday = weekdayMap[termType]!;
      const days = (targetWeekday - sellDateTime.weekday + 7) % 7;
      paymentDate = sellDateTime.plus({ days });
    }

    if (termType === PaymentTermType.SELECTED_DAY && selectedTermDay) {
      paymentDate = CommonHelper.getNextDayOfMonth(
        sellDateTime,
        selectedTermDay,
      );
    }

    if (termType === PaymentTermType.FIRST_DAY_OF_MONTH) {
      const isFirst = sellDateTime.day === 1;
      paymentDate = isFirst
        ? sellDateTime
        : sellDateTime.plus({ months: 1 }).startOf('month');
    }

    if (termType === PaymentTermType.LAST_DAY_OF_MONTH) {
      const endOfMonth = sellDateTime.endOf('month');
      const isLast = sellDateTime.day === endOfMonth.day;
      paymentDate = isLast ? sellDateTime : endOfMonth;
    }

    const finalDate = paymentDate.plus({ days: termDays });
    return [
      {
        name: datePipe.transform(finalDate, 'date-dot'),
        value: finalDate.toISODate()!,
      },
    ];
  }
}
